import { queryOptions, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { api } from "@/api/api";
import type { TripDetails } from "@/features/trips/types/types";
import type { FindPackageAvailabilityForRangeOutputDto } from "@/types/codegen";
import { getDateString } from "@/utils/dates";

/**
 * @param tripId Trip package ID
 * @param date Date string in ISO format, can omit day part e.g. '2025-11'
 */
export const tripAvailabilityQueryOptions = (tripId: string, date: string) => {
  const params = {
    rangeStart: getDateString(dayjs(date).startOf("month")),
    rangeEnd: getDateString(dayjs(date).endOf("month")),
  };

  return queryOptions({
    queryKey: ["packages", tripId, "availability", params],
    queryFn: ({ signal }) =>
      api.get<FindPackageAvailabilityForRangeOutputDto>(
        `/packages/packages/${tripId}/availability`,
        {
          signal,
          params,
        },
      ),
    select: (response) => response.data.data.availability,
    enabled: Boolean(tripId && date),
    staleTime: 1000 * 2, // 2 seconds
    throwOnError: false,
  });
};

export function useTripEffectiveStart(trip: TripDetails) {
  const allPossibleMonths = useMemo(() => {
    const months = [];
    const today = dayjs().startOf("month");
    const tripStart = dayjs(trip.startDate).startOf("month");
    let currentMonth = today.isAfter(tripStart) ? today : tripStart;
    const lastMonth = dayjs(trip.endDate).startOf("month");

    while (!lastMonth.isBefore(currentMonth, "month")) {
      months.push(currentMonth.format("YYYY-MM"));
      currentMonth = currentMonth.add(1, "month");
    }

    return months;
  }, [trip.startDate, trip.endDate]);

  const [lookupMonthIndex, setLookupMonthIndex] = useState(0);

  const monthsToFetch = allPossibleMonths.slice(
    lookupMonthIndex,
    lookupMonthIndex + 3,
  );

  const { data: effectiveStart = null, isLoading: isEffectiveStartLoading } =
    useQuery({
      queryKey: [
        "packages",
        trip.id,
        "availability",
        monthsToFetch,
        "effectiveStart",
      ] as const,
      queryFn: async ({ signal, client, queryKey: [, tripId, , months] }) => {
        const response =
          await api.get<FindPackageAvailabilityForRangeOutputDto>(
            `/packages/packages/${tripId}/availability`,
            {
              signal,
              params: {
                rangeStart: getDateString(dayjs(months[0]).startOf("month")),
                rangeEnd: getDateString(
                  dayjs(months[months.length - 1]).endOf("month"),
                ),
              },
            },
          );
        const availabilityData = response.data.data.availability;

        const groupedByMonth: Record<string, typeof availabilityData> = {};
        availabilityData.forEach((item) => {
          const month = dayjs(item.date).format("YYYY-MM");

          if (!groupedByMonth[month]) groupedByMonth[month] = [];
          groupedByMonth[month].push(item);
        });

        // Manually set query cache for each month so that individual month queries are cached
        Object.entries(groupedByMonth).forEach(([month, data]) => {
          const queryOptions = tripAvailabilityQueryOptions(trip.id, month);

          client.setQueryData(queryOptions.queryKey, {
            ...response,
            data: {
              data: {
                availability: data,
              },
              status: "success",
            },
          });
        });

        const effectiveStartMonth = months.find(
          (month) =>
            !!groupedByMonth[month] && groupedByMonth[month].length > 0,
        );

        if (!effectiveStartMonth) {
          setLookupMonthIndex((prev) => prev + monthsToFetch.length);

          return null;
        }

        const effectiveStartDate = groupedByMonth[effectiveStartMonth][0].date;

        return {
          month: effectiveStartMonth,
          date: effectiveStartDate,
        };
      },
      enabled: monthsToFetch.length > 0,
      staleTime: 1000 * 2, // 2 seconds
      throwOnError: false,
    });

  return {
    effectiveStartMonth: effectiveStart?.month ?? null,
    effectiveStartDate: effectiveStart?.date ?? null,
    isEffectiveStartLoading,
    allPossibleMonths,
  };
}
